/**
 * ⚠ PROVISIONAL VALUES — THE ONLY FILE ALLOWED TO CONTAIN THEM.
 *
 * Everything else in the planner reads real numbers out of the Bungie manifest.
 * The values here are the ones Bungie does not publish anywhere, so they are
 * estimates. They are quarantined here so they can be corrected in one place and
 * so no presentation component ever invents a number.
 *
 * If you find yourself typing a crafting constant into a component, put it here
 * instead — or better, check whether the manifest already has it (it usually
 * does; see docs/MANIFEST_NOTES.md).
 */

export interface ActivityEstimate {
  label: string;
  /** Weapon levels gained per completion. */
  progressPerCompletion: number;
}

export interface ProgressionEstimateConfig {
  estimatedKillsPerLevel: number;
  activityEstimates: ActivityEstimate[];
}

/**
 * ⚠ ESTIMATES, NOT GAME DATA.
 *
 * Weapon XP per level is not exposed by the Bungie API in any form — not in the
 * manifest, not in the profile components. These figures are rough
 * community-scale approximations and are deliberately round to signal that.
 * They also ignore XP bonuses, weapon-level boosts, and the fact that later
 * levels cost more, so treat them as order-of-magnitude only.
 *
 * The UI must always present numbers derived from these as estimates, and must
 * lead with `remainingLevels` (which *is* exact) rather than these.
 */
export const DEFAULT_PROGRESSION_ESTIMATE: ProgressionEstimateConfig = {
  estimatedKillsPerLevel: 100,
  activityEstimates: [
    { label: "Strike / Battleground", progressPerCompletion: 0.5 },
    { label: "Crucible match", progressPerCompletion: 0.4 },
    { label: "Gambit match", progressPerCompletion: 0.5 },
    { label: "Nightfall", progressPerCompletion: 0.8 },
  ],
};

/**
 * A flat resource cost charged for shaping a weapon, on top of the per-component
 * costs. Empty because none was found in the manifest: the recipe item carries
 * no `value` block and no top-level material requirement, so the shape cost
 * appears to be exactly the sum of the chosen components.
 *
 * If a base cost is ever confirmed, add it here — nowhere else.
 * See docs/MANIFEST_NOTES.md § "Known gap: the base shape cost".
 */
export const PROVISIONAL_BASE_SHAPE_COST: readonly { itemHash: number; quantity: number }[] = [];

/** Well-known material hashes, for icon ordering and the inventory panel.
 *  Read from the manifest at runtime; these are only for stable display order. */
export const MATERIAL_DISPLAY_ORDER: readonly number[] = [
  3159615086, // Glimmer
  3853748946, // Enhancement Core
  4257549984, // Enhancement Prism
  353704689, // Ascendant Alloy
];
